<template>
  <!--
    Generic mobile list (same look as the invoice / product / turnover mobile
    lists). Driven by the page through props: items + paging, and a `card(item)`
    function that describes each card. See docs/design-system/patterns/list-page.md.
  -->
  <div class="plm" dir="rtl">
    <div class="plm__header">
      <div class="plm__bar">
        <div class="plm__bar-actions">
          <button
            v-if="createLabel"
            type="button"
            class="plm__bar-btn plm__bar-btn--create"
            :aria-label="createLabel"
            @click="$emit('create')"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            v-if="printable"
            type="button"
            class="plm__bar-btn"
            :aria-label="$t('print')"
            @click="$emit('print')"
          >
            <v-icon size="20" color="#5B7C99">mdi-printer-outline</v-icon>
          </button>
          <button type="button" class="plm__bar-btn" :aria-label="$t('refresh')" @click="refresh">
            <v-icon size="20" color="#37474F">mdi-refresh</v-icon>
          </button>
        </div>
        <h1 class="plm__title">
          {{ title }}
          <small v-if="subtitle">{{ subtitle }}</small>
        </h1>
        <button type="button" class="plm__bar-btn plm__bar-btn--back" :aria-label="$t('back')" @click="goBack">
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>

      <div v-if="searchable || filterable" class="plm__search-row">
        <div v-if="searchable" class="plm__search">
          <v-icon size="20" color="#90A4AE" class="plm__search-icon">mdi-magnify</v-icon>
          <input
            :value="search"
            type="search"
            class="plm__search-input"
            :placeholder="$t('search')"
            autocomplete="off"
            @input="$emit('update:search', $event.target.value)"
          />
          <button
            v-if="search"
            type="button"
            class="plm__search-clear"
            :aria-label="$t('clear')"
            @click="$emit('update:search', '')"
          >
            <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
          </button>
        </div>
        <button
          v-if="filterable"
          type="button"
          class="plm__filter-btn"
          :class="{ 'plm__filter-btn--on': filterCount }"
          :aria-label="$t('filter')"
          @click="$emit('filter')"
        >
          <v-icon size="22" :color="filterCount ? '#1565C0' : '#546E7A'">mdi-filter-outline</v-icon>
          <span v-if="filterCount" class="plm__filter-dot" />
        </button>
      </div>

      <div v-if="summary && summary.length" class="plm-sum" :style="{ gridTemplateColumns: 'repeat(' + summary.length + ', 1fr)' }">
        <div v-for="(s, i) in summary" :key="i" class="plm-sum__col" :class="s.tone ? 'is-' + s.tone : ''">
          <span>{{ s.label }}</span>
          <strong class="plm-num">{{ s.value }}</strong>
        </div>
      </div>
    </div>

    <div v-if="loading && !displayItems.length" class="plm__state">
      <v-progress-circular indeterminate color="#5B7C99" size="28" />
    </div>
    <div v-else-if="!displayItems.length" class="plm__state plm__state--empty">
      <img class="plm__empty-logo" src="@/assets/dashboard/no-data-cloud.png" alt="" />
      <p class="plm__empty-text">{{ $t("no-data") }}</p>
    </div>
    <div v-else class="plm__list">
      <div v-if="total != null" class="plm__count">{{ faNum(group(total)) }} {{ $t("plRecords") }}</div>
      <article
        v-for="(item, index) in displayItems"
        :key="(item && item[itemKey]) != null ? item[itemKey] : index"
        class="plm-card"
        :class="cardOf(item).tone ? 'plm-card--' + cardOf(item).tone : ''"
        @click="openDetails(item)"
      >
        <div class="plm-card__head">
          <div class="plm-card__title-block">
            <span class="plm-card__name">{{ cardOf(item).title || "—" }}</span>
            <div v-if="cardOf(item).subtitle" class="plm-card__sub">{{ cardOf(item).subtitle }}</div>
          </div>
          <span v-if="cardOf(item).badge" class="plm-card__badge">{{ cardOf(item).badge }}</span>
        </div>
        <div v-if="(cardOf(item).chips || []).length" class="plm-card__chips">
          <span
            v-for="(c, ci) in cardOf(item).chips"
            :key="ci"
            :style="c.color ? { background: c.color, color: '#fff' } : null"
          >
            {{ c.text || c }}
          </span>
        </div>
        <div v-if="(cardOf(item).rows || []).length" class="plm-card__rows">
          <div v-for="(r, ri) in cardOf(item).rows" :key="ri" class="plm-card__row">
            <span>{{ r.label }}</span>
            <b :class="{ 'plm-num': r.num }">{{ r.value == null || r.value === "" ? "—" : r.value }}</b>
          </div>
        </div>
        <div v-if="(cardOf(item).amounts || []).length" class="plm-card__amounts">
          <div
            v-for="(a, ai) in cardOf(item).amounts"
            :key="ai"
            class="plm-card__amount"
            :class="a.tone ? 'is-' + a.tone : ''"
          >
            <span>{{ a.label }}</span>
            <b class="plm-num">{{ a.value }}</b>
          </div>
        </div>
      </article>
      <div v-if="appending" class="plm__state plm__state--more">
        <v-progress-circular indeterminate color="#5B7C99" size="22" />
      </div>
    </div>
    <div ref="sentinel" class="plm__sentinel" />

    <!-- Details -->
    <v-bottom-sheet v-model="detailSheet" scrollable>
      <v-card class="plm-sheet">
        <div class="plm-sheet__handle" />
        <div class="plm-sheet__header" dir="rtl">
          <span class="plm-sheet__header-spacer" />
          <div class="plm-sheet__title">{{ detailItem ? cardOf(detailItem).title : $t("details") }}</div>
          <button type="button" class="plm-sheet__back" :aria-label="$t('back')" @click="detailSheet = false">
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div v-if="detailItem && detailActions.length" class="plm-sheet__quickbar" dir="rtl">
          <button
            v-for="(a, i) in detailActions"
            :key="i"
            type="button"
            class="plm-sheet__quick"
            :class="{ 'plm-sheet__quick--danger': a.danger }"
            :disabled="a.disabled"
            @click="runAction(a)"
          >
            <v-icon size="18" :color="a.danger ? '#E53935' : '#5B7C99'">{{ a.icon }}</v-icon>
            {{ a.label }}
          </button>
        </div>
        <div v-if="detailItem" class="plm-sheet__body" dir="rtl">
          <div class="plm-sheet__card">
            <div v-for="(row, i) in detailRows" :key="i" class="plm-sheet__row">
              <span class="plm-sheet__row-label">{{ row.label }}</span>
              <span class="plm-sheet__row-value" :class="{ 'plm-num': row.num }">
                {{ row.value == null || row.value === "" ? "—" : row.value }}
              </span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
export default {
  name: "ProListMobile",
  props: {
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    items: { type: Array, default: () => [] },
    itemKey: { type: String, default: "id" },
    total: { type: Number, default: null },
    loading: { type: Boolean, default: false },
    page: { type: Number, default: 1 },
    pageCount: { type: Number, default: 1 },
    search: { type: String, default: null },
    searchable: { type: Boolean, default: true },
    filterable: { type: Boolean, default: true },
    filterCount: { type: Number, default: 0 },
    printable: { type: Boolean, default: false },
    createLabel: { type: String, default: "" },
    // [{ label, value, tone: 'pos'|'neg'|'' }]
    summary: { type: Array, default: null },
    // item -> { title, subtitle, badge, tone: 'pos'|'neg', chips[], rows[{label,value,num}],
    //           amounts[{label,value,tone}], details[{label,value,num}] }
    card: { type: Function, required: true },
    // item -> [{ icon, label, handler(item), danger, disabled }]
    actions: { type: Function, default: null },
  },
  data: () => ({
    displayItems: [],
    appending: false,
    observer: null,
    detailSheet: false,
    detailItem: null,
  }),
  computed: {
    detailActions() {
      return this.detailItem && this.actions ? this.actions(this.detailItem) || [] : [];
    },
    detailRows() {
      if (!this.detailItem) return [];
      var c = this.cardOf(this.detailItem);
      if (c.details) return c.details;
      return [].concat(c.subtitle ? [{ label: this.$t("description"), value: c.subtitle }] : [], c.rows || [], (c.amounts || []).map(function (a) {
        return { label: a.label, value: a.value, num: true };
      }));
    },
  },
  watch: {
    items(val) {
      var items = val || [];
      if (this.appending) {
        var key = this.itemKey;
        var seen = {};
        this.displayItems.forEach(function (i) { seen[i[key]] = true; });
        this.displayItems = this.displayItems.concat(items.filter(function (i) { return !seen[i[key]]; }));
        this.appending = false;
      } else {
        this.displayItems = items.slice();
      }
    },
    search() {
      this.appending = false;
    },
    loading(val) {
      if (!val && this.appending && this.page <= 1) this.appending = false;
    },
  },
  // This screen has its own sticky bars; hide the app bottom navigation.
  activated() {
    this.$store.commit("SET_BOTTOMNAVIGATE", false);
  },
  deactivated() {
    this.$store.commit("SET_BOTTOMNAVIGATE", true);
  },
  mounted() {
    this.$store.commit("SET_BOTTOMNAVIGATE", false);
    this.displayItems = (this.items || []).slice();
    this.$nextTick(() => {
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0] && entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeDestroy() {
    this.$store.commit("SET_BOTTOMNAVIGATE", true);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    cardOf(item) {
      try {
        return this.card(item) || {};
      } catch (e) {
        return {};
      }
    },
    faNum(v) {
      var s = String(v == null ? "" : v);
      return String(this.$i18n && this.$i18n.locale).indexOf("fa") === 0
        ? s.replace(/[0-9]/g, function (d) { return "۰۱۲۳۴۵۶۷۸۹"[d]; })
        : s;
    },
    group(n) {
      return String(Number(n) || 0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ name: "Home" }).catch(() => {});
    },
    refresh() {
      this.appending = false;
      this.$emit("refresh");
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    loadMore() {
      if (this.loading || this.appending) return;
      if (this.page >= (this.pageCount || 1)) return;
      this.appending = true;
      this.$emit("load-more", this.page + 1);
    },
    openDetails(item) {
      this.detailItem = item;
      this.detailSheet = true;
    },
    runAction(a) {
      var item = this.detailItem;
      this.detailSheet = false;
      if (a.handler) a.handler(item);
    },
  },
};
</script>

<style scoped>
.plm {
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
  background: #ffffff;
  min-height: calc(100vh - 52px);
  box-sizing: border-box;
  padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
}
.plm--selecting {
  padding-bottom: calc(140px + env(safe-area-inset-bottom, 0px));
}
.plm__header {
  position: sticky;
  top: 52px;
  z-index: 4;
  background: #ffffff;
  padding: 6px 0 10px;
  border-bottom: 1px solid #eef1f4;
  margin-bottom: 10px;
}
.plm__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}
.plm__bar-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 80px;
}
.plm__bar-btn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.plm__bar-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.plm__bar-btn--create {
  background: rgba(91, 124, 153, 0.1);
}
.plm__bar-btn--back {
  margin-inline-start: auto;
}
.plm__title {
  flex: 1 1 auto;
  margin: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.plm__search-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.plm__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 6px;
  background: #f3f5f7;
}
.plm__search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #37474f;
}
.plm__search-clear {
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
}
.plm__filter-btn {
  position: relative;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border: 1px solid #e0e4e8;
}
.plm__filter-btn:active {
  background: rgba(91, 124, 153, 0.08);
}
.plm__filter-btn--on {
  border-color: rgba(91, 124, 153, 0.45);
  background: rgba(91, 124, 153, 0.08);
}
.plm__filter-dot {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.plm__select-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 4px 0;
}
.plm__select-bar-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.08);
  font-size: 11px;
  font-weight: 600;
  color: #455a64;
  cursor: pointer;
}
.plm__select-bar-btn:active {
  background: rgba(91, 124, 153, 0.16);
}
.plm__select-count {
  flex: 1 1 auto;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.plm__state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
  color: #607d8b;
  font-size: 13px;
}
.plm__state--empty {
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(100vh - 220px);
  padding: 32px 16px 24px;
  background: #ffffff;
}
.plm__state--more {
  padding: 14px 0;
}
.plm__empty-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.85;
}
.plm__empty-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
  text-align: center;
}
.plm__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}
.plm__sentinel {
  height: 1px;
}
.plm-card {
  position: relative;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: #f8fafc;
  padding: 12px;
  transition: transform 0.15s ease;
}
.plm-card--selected {
  transform: scale(0.99);
  border-color: rgba(21, 101, 192, 0.45);
}
.plm-card__select-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-card__select-overlay--on {
  background: rgba(38, 50, 56, 0.12);
}
.plm-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0d47a1;
  box-shadow: 0 2px 6px rgba(13, 71, 161, 0.4);
}
.plm-bulkbar {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: calc(72px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.14);
}
.plm-bulkbar__btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
  padding: 6px 2px;
  border: 0;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-bulkbar__btn:active {
  background: rgba(91, 124, 153, 0.15);
}
.plm-bulkbar__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.plm-bulkbar__btn--danger {
  color: #e53935;
  background: rgba(229, 57, 53, 0.06);
}
.plm-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.plm-card__title-block {
  min-width: 0;
  flex: 1 1 auto;
}
.plm-card__name-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-card__name-link:disabled {
  cursor: default;
}
.plm-card__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: right;
}
.plm-card__sub {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.plm-card__number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 6px;
  padding: 2px 8px;
}
.plm-card__amounts {
  margin-top: 10px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.plm-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.plm-card__amount-label {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.plm-card__amount-value {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.plm-card__status-pill {
  display: inline-flex;
  align-items: center;
  max-width: 62%;
  height: 24px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.plm-card__status-pill--empty {
  background: rgba(144, 164, 174, 0.12);
  color: #90a4ae;
  border-color: transparent;
}
.plm-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.plm-card__actions {
  display: flex;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
  flex-wrap: wrap;
}
.plm-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 34px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 0;
  -webkit-tap-highlight-color: transparent;
}
.plm-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.plm-btn--danger {
  border: 1px solid rgba(229, 57, 53, 0.35);
  background: rgba(229, 57, 53, 0.06);
  color: #c62828;
}
.plm-btn--primary {
  background: #5b7c99;
  color: #ffffff;
}
.plm-btn--outline {
  background: #ffffff;
  color: #5b7c99;
  border: 1px solid #cfd8dc;
}
.plm-rel-btn {
  flex: 0 0 auto;
  height: 28px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.08);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-rel-btn:active {
  background: rgba(91, 124, 153, 0.16);
}
.plm-rel-btn:disabled {
  background: transparent;
  color: #90a4ae;
  opacity: 0.55;
  cursor: default;
}
.plm-rel-empty {
  padding: 18px 0;
  font-size: 12.5px;
  color: #90a4ae;
  text-align: center;
}
.plm-rel-item {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 10px;
}
.plm-rel-item__title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
}
.plm-rel-item__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;
}
.plm-rel-item__key {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.plm-rel-item__val {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.plm-sheet {
  border-radius: 6px 6px 0 0 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.plm-sheet__handle {
  flex: 0 0 auto;
  width: 44px;
  height: 4px;
  border-radius: 6px;
  background: #cfd8dc;
  margin: 10px auto 2px;
}
.plm-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 10px;
  border-bottom: 1px solid #eef1f4;
}
.plm-sheet__header-side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  min-width: 0;
}
.plm-sheet__header-side--end {
  justify-content: flex-end;
}
.plm-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.plm-sheet__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.plm-sheet__num-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  background: rgba(135, 158, 182, 0.14);
  border: 1px solid rgba(135, 158, 182, 0.28);
  color: #5b7c99;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.plm-sheet__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.plm-sheet__back:active {
  background: rgba(91, 124, 153, 0.1);
}
.plm-sheet__quickbar {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
}
.plm-sheet__quick {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 46px;
  padding: 4px 2px;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.plm-sheet__quick:disabled {
  opacity: 0.45;
  cursor: default;
}
.plm-sheet__quick:active:not(:disabled) {
  background: rgba(91, 124, 153, 0.14);
}
.plm-sheet__name-block {
  flex: 0 0 auto;
  background: #edf2f7;
  border-radius: 6px;
  padding: 10px 14px;
  margin: 6px 16px 0;
}
.plm-sheet__name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.plm-sheet__name-value {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 70%;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-sheet__name-block .plm-sheet__linkrow {
  margin: 8px 0 0;
  background: rgba(255, 255, 255, 0.75);
}
.plm-sheet__body {
  overflow-y: auto;
  padding: 8px 16px 20px;
}
.plm-sheet__card {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 6px;
  padding: 4px 14px;
}
.plm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
}
.plm-sheet__card .plm-sheet__row:last-child {
  border-bottom: 0;
}
.plm-sheet__row--band {
  margin: 0 -14px;
  padding: 8px 14px;
  border-bottom-color: rgba(91, 124, 153, 0.12);
  background: rgba(91, 124, 153, 0.05);
}
.plm-sheet__row-label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.plm-sheet__row-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.plm-sheet__tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
  max-width: 62%;
}
.plm-sheet__linkrow {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  margin: 6px 0 2px;
  padding: 0 10px;
  border: 1px solid rgba(0, 150, 136, 0.16);
  border-radius: 6px;
  background: rgba(0, 150, 136, 0.07);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-sheet__linkrow-text {
  flex: 1 1 auto;
  font-size: 12.5px;
  font-weight: 700;
  color: #00796b;
  text-align: right;
}
.plm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.plm-sheet__actions--sheet {
  padding-top: 4px;
}
.plm-sheet__action {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 42px;
  padding: 0 6px;
  border: 0;
  border-bottom: 1px solid #e8edf2;
  border-radius: 6px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.plm-sheet__action:disabled {
  opacity: 0.45;
  cursor: default;
}
.plm-sheet__action:active:not(:disabled) {
  background: rgba(91, 124, 153, 0.08);
}
.plm-sheet__action--danger {
  color: #c62828;
}
.plm-sheet__action--log {
  cursor: default;
  padding: 0;
}
.plm-sheet__action--log > div {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.plm-sheet__action--log ::v-deep .v-list-item {
  flex: 1 1 auto;
  min-height: 42px;
  height: 42px;
  padding: 0;
}
.plm-sheet__action--log ::v-deep .v-list-item__icon {
  margin: 0 !important;
  min-width: 19px;
  align-self: center;
}
.plm-sheet__action--log ::v-deep .v-list-item__icon .v-icon {
  font-size: 19px !important;
  color: #5b7c99 !important;
}
.plm-sheet__action--log ::v-deep .v-list-item__title {
  margin-inline-start: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
}
.plm-sheet--filter {
  max-height: 92vh;
  border-radius: 6px 6px 0 0 !important;
}
.plm-sheet__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef1f4;
  background: #ffffff;
}
.plm-sheet__btn {
  flex: 1 1 0;
  height: 44px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-sheet__btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.plm-sheet__btn--primary:active {
  background: #4d6a84;
}
.plm-sheet__btn--ghost {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #546e7a;
}
.plm-sheet__btn--ghost:active {
  background: rgba(91, 124, 153, 0.08);
}
.plm-filter-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #ffffff;
}
.plm-filter-nav__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.plm-filter-nav__item:active {
  background: #f7f9fc;
}
.plm-filter-nav__item:last-child {
  border-bottom: 0;
}
.plm-filter-nav__icon {
  flex: 0 0 auto;
}
.plm-filter-nav__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.plm-filter-nav__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}
.plm-filter-nav__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-filter-nav__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.plm-filter-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px 6px;
  background: #ffffff;
}
.plm-filter-fields > * {
  margin-bottom: 14px;
}
.plm-picker-host {
  display: none !important;
}
.plm-picker-overlay {
  position: relative;
  height: 40px;
  overflow: hidden;
  border-radius: 6px;
}
.plm-picker-cover {
  position: absolute !important;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
}
.plm-picker-cover__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}
.plm-f-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.plm-f-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 4px;
}
.plm-f-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.plm-f-picker-field:active {
  background: #eef4f8;
  border-color: #90caf9;
}
.plm-f-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-select-layer {
  padding: 6px 16px 20px;
}
.plm-select-layer__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.plm-select-layer__text {
  font-size: 13.5px;
  font-weight: 500;
  color: #37474f;
}
.plm-select-layer__clear {
  display: block;
  width: 100%;
  padding: 14px 4px 4px;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #c62828;
  text-align: center;
  cursor: pointer;
}
.plm-mobile-date {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
  background: #ffffff;
}
.plm-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 6px;
  background: #f7f9fc;
}
.plm-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.plm-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
}
.plm-mobile-date__clear {
  border: 0;
  background: transparent;
  color: #e91e63;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
}
.plm-mobile-date__picker {
  width: 100%;
}
.plm-mobile-date__picker ::v-deep .vpd-main,
.plm-mobile-date__picker ::v-deep .vpd-wrapper,
.plm-mobile-date__picker ::v-deep .vpd-container,
.plm-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
}
.plm-confirm-sheet {
  border-radius: 6px 6px 0 0 !important;
  padding: 8px 16px 16px;
  background: #fff !important;
}
.plm-confirm-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 14px;
  border-radius: 6px;
  background: #cfd8dc;
}
.plm-confirm-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}
.plm-confirm-sheet__title {
  text-align: center;
  font-size: 16px;
  font-weight: 800;
  color: #263238;
  margin-bottom: 4px;
}
.plm-confirm-sheet__subtitle {
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: #78909c;
  margin: 0 0 14px;
}
.plm-confirm-sheet__rows {
  border: 1px solid #e8edf2;
  border-radius: 6px;
  background: #f8fafc;
  padding: 4px 12px;
  margin-bottom: 14px;
}
.plm-confirm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
  font-size: 13px;
  color: #546e7a;
}
.plm-confirm-sheet__row:last-child {
  border-bottom: 0;
}
.plm-confirm-sheet__row strong {
  color: #263238;
  font-weight: 700;
  text-align: left;
}
.plm-confirm-sheet__items {
  max-height: 160px;
  overflow-y: auto;
  padding: 4px 0 8px;
}
.plm-confirm-sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px dashed #e0e4e8;
}
.plm-confirm-sheet__item:last-child {
  border-bottom: 0;
}
.plm-confirm-sheet__item-main {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.plm-confirm-sheet__item-index {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(91, 124, 153, 0.12);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.plm-confirm-sheet__item-name {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-confirm-sheet__item-number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.plm-confirm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.plm-confirm-sheet__yes {
  border-radius: 6px !important;
}
.plm-confirm-sheet__no {
  border-radius: 6px !important;
}

/* Accounting-specific additions */
.plm__count {
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
  padding: 2px 4px 0;
}
.plm-card__head-side {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.plm-card__sub--clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}
.plm-doc-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  color: #b26a00;
  background: rgba(255, 179, 0, 0.14);
}
.plm-doc-pill--always {
  color: #2e7d32;
  background: rgba(46, 125, 50, 0.12);
}
.plm-amount,
.plm-ltr {
  direction: ltr;
  font-variant-numeric: tabular-nums;
}
.plm-sum {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  border-radius: 6px;
  background: rgba(91, 124, 153, 0.08);
}
.plm-sum--warn {
  background: rgba(255, 179, 0, 0.12);
}
.plm-sum__col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.plm-sum__col span {
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
}
.plm-sum__col strong {
  font-size: 13px;
  color: #263238;
  direction: ltr;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-f-pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
}
.plm-f-input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
  outline: none;
  direction: ltr;
  text-align: right;
  font-size: 14px;
  color: #37474f;
}
.plm-f-input:focus {
  border-color: #5b7c99;
}

/* Turnover-specific */
.plm__title small {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-sum {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 8px;
}
.plm-sum__col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 7px 9px;
  border-radius: 6px;
  background: #f3f5f7;
}
.plm-sum__col span {
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
}
.plm-sum__col strong {
  font-size: 12.5px;
  color: #263238;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-sum__col--neg strong {
  color: #c62828;
}
.plm-amount {
  direction: ltr;
  unicode-bidi: isolate;
  font-variant-numeric: tabular-nums;
}
.plm-card {
  cursor: pointer;
  border-inline-start: 3px solid transparent;
}
.plm-card--deb {
  border-inline-start-color: #2e7d32;
}
.plm-card--cred {
  border-inline-start-color: #c2185b;
}
.plm-card__date {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 6px;
  padding: 2px 8px;
}
.plm-card__sub--clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}
.plm-card__accounts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.plm-card__accounts span {
  font-size: 11px;
  font-weight: 600;
  color: #546e7a;
  background: #f3f5f7;
  border-radius: 6px;
  padding: 2px 7px;
}
.plm-card__amounts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
}
.plm-card__amount {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.plm-card__amount span {
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
}
.plm-card__amount b {
  font-size: 12.5px;
  color: #263238;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.plm-card__amount.is-zero b {
  color: #b0bec5;
  font-weight: 500;
}
.plm-card__amount--remain b {
  color: #1565c0;
}
.plm-card__amount--remain.is-neg b {
  color: #c62828;
}
.plm__count {
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
  padding: 2px 4px 0;
}

/* Generic card parts */
.plm-num {
  direction: ltr;
  unicode-bidi: isolate;
  font-variant-numeric: tabular-nums;
}
.plm-card--pos {
  border-inline-start: 3px solid #2e7d32;
}
.plm-card--neg {
  border-inline-start: 3px solid #c2185b;
}
.plm-card__badge {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #5b7c99;
  background: rgba(91, 124, 153, 0.1);
  border-radius: 6px;
  padding: 2px 8px;
  white-space: nowrap;
}
.plm-card__sub {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.plm-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.plm-card__chips span {
  font-size: 11px;
  font-weight: 600;
  color: #546e7a;
  background: #f3f5f7;
  border-radius: 6px;
  padding: 2px 7px;
}
.plm-card__rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
}
.plm-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
}
.plm-card__row span {
  font-weight: 600;
  color: #78909c;
}
.plm-card__row b {
  font-weight: 700;
  color: #37474f;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 65%;
}
.plm-card__amount.is-pos b,
.plm-sum__col.is-pos strong {
  color: #2e7d32;
}
.plm-card__amount.is-neg b,
.plm-sum__col.is-neg strong {
  color: #c62828;
}
.plm-card__amount.is-info b {
  color: #1565c0;
}
.plm-sheet__quick--danger {
  color: #c62828 !important;
}
</style>
